import type { DbExecutor } from "./executor";
import type { ScopePackageRepository } from "./repositories/scopePackageRepository";
import type { FeatureRepository } from "./repositories/featureRepository";
import type { AssemblyRepository } from "./repositories/assemblyRepository";
import type { RoomRepository } from "./repositories/roomRepository";
import type { CatalogRepository } from "./repositories/catalogRepository";
import type { DesignScopeCoordinator } from "./designScopeCoordinator";
import { ValidationError } from "./repositories/projectRepository";
import { generateId } from "./ids";
import {
  planPackageApplication,
  type PackagePlan,
  type PackagePlanContext,
  type PlannedItem,
} from "../engine/packagePlan";
import type { ScopePackage, ScopePackageItemInput } from "../domain/scopePackage";
import type { FeatureAction, QuantityUnit } from "../domain/feature";
import { effectiveMeasurement, effectiveWallHeight, grossWallAreaX100 } from "../domain/room";
import type { Cents } from "../domain/money";
import type { ScopeSession } from "../design/scopeSession";
import { archiveObject } from "../geometry";

/**
 * Applying and un-applying Scope Packages (Phase 10, spec §21A).
 *
 * A package application creates ORDINARY project scope — the same Features,
 * geometry bindings and assembly instances the user could create by hand —
 * and records provenance. It never becomes a runtime dependency: after
 * applying, the project is normal editable scope, and editing the library
 * package later cannot reach back into it (copy-on-use).
 *
 * Spatial scope stays Design-master: objects are never conjured outside the
 * plan. An item whose object already exists is reconciled (Replace); one
 * whose object does not exist is queued for guided placement in Design.
 */

export interface PackagePreviewItem extends PlannedItem {
  /** Display name for the preview row. */
  label: string;
  /** Estimated cost from the ASSEMBLY ENGINE — never package math. */
  estimateCents: Cents | null;
  /** Why there is no estimate yet (never a silent $0). */
  estimateNote: string | null;
  /** Spec §89: the unit a typed quantity for this item is entered in (null = not enterable). */
  pricingUnit: QuantityUnit | null;
}

export interface PackagePreview {
  package: ScopePackage;
  plan: PackagePlan;
  items: PackagePreviewItem[];
  /** Sum of the estimable items — explicitly incomplete when some are not. */
  estimatedTotalCents: Cents;
  unpricedCount: number;
  placementCount: number;
  ambiguousCount: number;
  productSelectionCount: number;
  roomName: string;
  projectType: "renovation" | "new_construction";
}

export interface ApplyResult {
  applicationId: string;
  createdFeatureIds: string[];
  reusedFeatureIds: string[];
  scopedObjectIds: string[];
  pendingPlacements: PendingPlacement[];
}

export interface PendingPlacement {
  itemId: string;
  objectDefinitionId: string;
  objectDefinitionName: string;
  featureDefinitionId: string | null;
  action: FeatureAction;
  remaining: number;
}

interface ItemSnapshot {
  itemId: string;
  /** The quantity the PACKAGE set (so a later change is the user, not us). */
  quantityX100?: number | null;
  /** Filled in after applying: the scope this item produced. */
  featureId?: string | null;
  featureDefinitionId: string | null;
  objectDefinitionId: string | null;
  objectCount: number;
  action: FeatureAction;
  assemblyTemplateId: string | null;
  preferredProductId: string | null;
  disposition: string;
}

export class PackageService {
  constructor(
    private readonly db: DbExecutor,
    private readonly packages: ScopePackageRepository,
    private readonly features: FeatureRepository,
    private readonly assemblies: AssemblyRepository,
    private readonly rooms: RoomRepository,
    private readonly catalog: CatalogRepository,
    private readonly designScope: DesignScopeCoordinator,
  ) {}

  /* ------------------------------------------------------------------ */
  /* Planning + preview                                                  */
  /* ------------------------------------------------------------------ */

  async context(projectId: string, roomId: string): Promise<PackagePlanContext> {
    const projectRows = await this.db.select<{ project_type: string; active_design_id: string | null }>(
      "SELECT project_type, active_design_id FROM projects WHERE id = ?",
      [projectId],
    );
    if (projectRows.length === 0) throw new ValidationError(["That project no longer exists."]);
    const room = await this.rooms.getById(roomId);
    if (!room) throw new ValidationError(["That room no longer exists."]);
    const designId = projectRows[0].active_design_id;

    const features = await this.features.listByProject(projectId);
    const existingFeatures = features
      .filter((f) => f.archivedAt === null && f.roomId === roomId && (f.designId === null || f.designId === designId))
      .map((f) => ({ id: f.id, featureDefinitionId: f.featureDefinitionId, name: f.name, designId: f.designId }));

    // Room containment comes from the geometry coordinator (plan_objects
    // caches a room only once scope is bound), so a package sees the same
    // "what is already in this room" the Design does.
    const objectRows = designId
      ? await this.db.select<{ id: string; definition_id: string; feature_id: string | null; origin_id: string | null; display_name: string; floor_id: string }>(
          `SELECT o.id, o.definition_id, o.feature_id, o.origin_id, o.floor_id, d.display_name
             FROM plan_objects o JOIN object_definitions d ON d.id = o.definition_id
            WHERE o.design_id = ? AND o.archived_at IS NULL`,
          [designId],
        )
      : [];
    const roomOfObject = new Map<string, string | null>();
    if (designId) {
      for (const floorId of new Set(objectRows.map((o) => o.floor_id))) {
        try {
          for (const [objectId, containing] of await this.designScope.roomIdsForObjects(designId, floorId)) {
            roomOfObject.set(objectId, containing);
          }
        } catch (err) {
          console.error("Room containment could not be resolved:", err);
        }
      }
    }
    const objectsInRoom = objectRows.filter((o) => roomOfObject.get(o.id) === roomId);

    const geometry = designId
      ? await this.db.select<{ floor_area_x100: number | null; perimeter_x100: number | null; status: string }>(
          "SELECT floor_area_x100, perimeter_x100, status FROM design_room_geometry WHERE design_id = ? AND room_id = ?",
          [designId, roomId],
        )
      : [];
    /* THE ROOM'S EFFECTIVE MEASUREMENT, not only its drawing (spec §86
     * audit). A room somebody measured and typed in — the way anyone who
     * has not drawn a plan describes a house — was told "no geometry yet, so
     * the quantity stays blank", while the coordinator that binds the item
     * afterwards (effectiveFloorArea) would have used that very figure. One
     * rule, the domain's: geometry when bound, else the typed measurement. */
    const bound = geometry[0] && geometry[0].status === "bound"
      ? {
          floorAreaX100: geometry[0].floor_area_x100 === null ? null : Number(geometry[0].floor_area_x100),
          perimeterX100: geometry[0].perimeter_x100 === null ? null : Number(geometry[0].perimeter_x100),
        }
      : null;
    const effectiveArea = effectiveMeasurement(room.floorAreaX100, room.floorAreaSource, bound?.floorAreaX100 ?? null);
    const effectivePerim = effectiveMeasurement(room.perimeterX100, room.perimeterSource, bound?.perimeterX100 ?? null);
    // Spec §89: wall surface from the SAME effective perimeter and the room's
    // (or its floor's) wall height — the formula the scope coordinator binds with.
    const floorRows = await this.db.select<{ h: number | null }>("SELECT default_ceiling_height_inches AS h FROM floors WHERE id = ?", [room.floorId]);
    const wallHeight = effectiveWallHeight(room, floorRows[0]?.h === null || floorRows[0]?.h === undefined ? null : Number(floorRows[0].h));

    const featureDefs = await this.db.select<{ id: string; display_name: string; default_action: string }>(
      "SELECT id, display_name, default_action FROM feature_definitions",
    );
    const objectDefs = await this.db.select<{ id: string; display_name: string }>(
      "SELECT id, display_name FROM object_definitions",
    );

    return {
      projectType: projectRows[0].project_type === "new_construction" ? "new_construction" : "renovation",
      roomId,
      roomName: room.name,
      roomTypeId: room.roomTypeId,
      existingFeatures,
      existingObjects: objectsInRoom.map((o) => ({
        id: o.id,
        definitionId: o.definition_id,
        featureId: o.feature_id,
        originId: o.origin_id,
        label: o.display_name,
      })),
      roomFloorAreaX100: effectiveArea.valueX100,
      roomPerimeterX100: effectivePerim.valueX100,
      roomWallAreaX100: effectivePerim.valueX100 === null ? null : grossWallAreaX100(effectivePerim.valueX100, wallHeight.inches),
      roomWallHeightAssumed: wallHeight.source === "assumed",
      hasProposedDesign: designId !== null,
      featureDefinitionNames: new Map(featureDefs.map((d) => [d.id, d.display_name])),
      objectDefinitionNames: new Map(objectDefs.map((d) => [d.id, d.display_name])),
    };
  }

  /**
   * What applying this package to this room would do, with the estimate the
   * project's own engine produces. Nothing is written, and the preview is
   * recomputed each time it is opened — it is never a frozen quote.
   */
  async preview(
    projectId: string,
    roomId: string,
    packageId: string,
    selections: Map<string, boolean> = new Map(),
    objectChoices: Map<string, string> = new Map(),
    /** Spec §89: quantities typed in the preview, per item, in the item's pricing unit. */
    quantityEntries: Map<string, number> = new Map(),
  ): Promise<PackagePreview> {
    const pkg = await this.packages.getById(packageId);
    if (!pkg) throw new ValidationError(["That package no longer exists."]);
    const context = await this.context(projectId, roomId);
    context.itemUnits = await this.itemUnits(pkg.items);
    const defaultActions = await this.defaultActions();
    const plan = planPackageApplication(pkg.items, context, selections, objectChoices, defaultActions, quantityEntries);

    const items: PackagePreviewItem[] = [];
    let total = 0;
    let unpriced = 0;
    let productSelections = 0;
    for (const planned of plan.items) {
      const label =
        (planned.item.featureDefinitionId && context.featureDefinitionNames.get(planned.item.featureDefinitionId)) ??
        (planned.item.objectDefinitionId && context.objectDefinitionNames.get(planned.item.objectDefinitionId)) ??
        "Scope item";
      let estimateCents: Cents | null = null;
      let estimateNote: string | null = null;
      if (planned.included && planned.disposition !== "skipped") {
        const templateId = planned.item.assemblyTemplateId ?? (await this.singleCompatibleTemplate(planned));
        if (templateId === null) {
          estimateNote = "No priced assembly yet — attach one after applying.";
        } else if (planned.effectiveQuantityX100 === null) {
          estimateNote = "Quantity not known yet, so no estimate is shown.";
        } else {
          const estimate = await this.assemblies.estimateTemplate(templateId, planned.effectiveQuantityX100, projectId);
          if (estimate === null) {
            estimateNote = "This assembly cannot price that quantity's unit.";
          } else {
            estimateCents = estimate;
            total += estimate;
          }
        }
        if (estimateCents === null) unpriced += 1;
        if (planned.item.preferredProductId === null && planned.item.objectDefinitionId === null && templateId !== null) {
          // Materials still needing a product are reported by Materials;
          // here we only count the package's explicit preferences.
        }
        if (planned.item.preferredProductId !== null) productSelections += 1;
      }
      items.push({ ...planned, label, estimateCents, estimateNote, pricingUnit: context.itemUnits.get(planned.itemId) ?? null });
    }

    return {
      package: pkg,
      plan,
      items,
      estimatedTotalCents: total,
      unpricedCount: unpriced,
      placementCount: plan.placementCount,
      ambiguousCount: plan.ambiguousCount,
      productSelectionCount: productSelections,
      roomName: context.roomName,
      projectType: context.projectType,
    };
  }

  /**
   * The unit each item is priced in: its own assembly's driving unit, else
   * the definition's single suggested template's. Lets the preview ask for
   * "24 linear ft" rather than a drawing (spec §89).
   */
  private async itemUnits(items: ScopePackage["items"]): Promise<Map<string, QuantityUnit>> {
    const units = new Map<string, QuantityUnit>();
    for (const item of items) {
      let templateUnit: string | null = null;
      if (item.assemblyTemplateId) {
        templateUnit = (await this.assemblies.getTemplate(item.assemblyTemplateId))?.drivingUnit ?? null;
      } else if (item.featureDefinitionId) {
        const suggested = await this.assemblies.listSuggestedTemplates(item.featureDefinitionId);
        if (suggested.length === 1) templateUnit = suggested[0].drivingUnit;
      }
      if (templateUnit) units.set(item.id, templateUnit as QuantityUnit);
    }
    return units;
  }

  private async defaultActions(): Promise<Map<string, FeatureAction>> {
    const rows = await this.db.select<{ id: string; default_action: string }>(
      "SELECT id, default_action FROM feature_definitions",
    );
    return new Map(rows.map((r) => [r.id, r.default_action as FeatureAction]));
  }

  /** The Phase 8.1 rule: exactly one unit-compatible suggested template. */
  private async singleCompatibleTemplate(planned: PlannedItem): Promise<string | null> {
    if (!planned.item.featureDefinitionId) return null;
    const unit = unitForPlanned(planned);
    const compatible = (await this.assemblies.listSuggestedTemplates(planned.item.featureDefinitionId)).filter(
      (t) => unit === null || t.drivingUnit === unit,
    );
    return compatible.length === 1 ? compatible[0].id : null;
  }

  /* ------------------------------------------------------------------ */
  /* Applying                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Apply the package to the room. Room-bound scope is created immediately;
   * spatial scope whose object already exists is reconciled when a plan
   * session is supplied (the Design workflow), and anything still missing
   * an object is returned as guided placement work.
   */
  async apply(
    projectId: string,
    roomId: string,
    packageId: string,
    options: {
      selections?: Map<string, boolean>;
      objectChoices?: Map<string, string>;
      /** Supplied when applying from Design: lets existing objects be scoped now. */
      session?: ScopeSession | null;
      /** Spec §89: quantities typed in the preview, per item. */
      quantityEntries?: Map<string, number>;
    } = {},
  ): Promise<ApplyResult> {
    const pkg = await this.packages.getById(packageId);
    if (!pkg) throw new ValidationError(["That package no longer exists."]);
    const context = await this.context(projectId, roomId);
    context.itemUnits = await this.itemUnits(pkg.items);
    const defaultActions = await this.defaultActions();
    const typedQuantities = options.quantityEntries ?? new Map<string, number>();
    const plan = planPackageApplication(
      pkg.items,
      context,
      options.selections ?? new Map(),
      options.objectChoices ?? new Map(),
      defaultActions,
      typedQuantities,
    );
    const designId = (
      await this.db.select<{ active_design_id: string | null }>("SELECT active_design_id FROM projects WHERE id = ?", [projectId])
    )[0]?.active_design_id ?? null;

    const createdFeatureIds: string[] = [];
    const reusedFeatureIds: string[] = [];
    const scopedObjectIds: string[] = [];
    const applicationId = generateId();
    const now = new Date().toISOString();

    const produced = new Map<string, string>();
    await this.db.transaction(async () => {
      const snapshot: ItemSnapshot[] = plan.items
        .filter((p) => p.included && p.disposition !== "skipped")
        .map((p) => ({
          itemId: p.itemId,
          quantityX100: p.quantityX100,
          featureId: null as string | null,
          featureDefinitionId: p.item.featureDefinitionId,
          objectDefinitionId: p.item.objectDefinitionId,
          objectCount: Math.max(1, p.item.objectCount),
          action: p.action,
          assemblyTemplateId: p.item.assemblyTemplateId,
          preferredProductId: p.item.preferredProductId,
          disposition: p.disposition,
        }));
      await this.db.execute(
        `INSERT INTO package_applications
           (id, project_id, design_id, room_id, package_id, package_name_snapshot, finish_tier_snapshot,
            item_snapshot_json, applied_at, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [applicationId, projectId, designId, roomId, pkg.id, pkg.name, pkg.finishTier, JSON.stringify(snapshot), now, now, now],
      );

      for (const planned of plan.items) {
        if (!planned.included || planned.disposition === "skipped" || planned.disposition === "ambiguous") continue;

        if (planned.disposition === "reuse-feature" && planned.existingFeatureId) {
          // Reuse means REUSE: the existing feature's own configuration is
          // left alone; only its package provenance is recorded.
          reusedFeatureIds.push(planned.existingFeatureId);
          produced.set(planned.itemId, planned.existingFeatureId);
          await this.db.execute(
            "UPDATE project_features SET package_application_id = COALESCE(package_application_id, ?), updated_at = ? WHERE id = ?",
            [applicationId, now, planned.existingFeatureId],
          );
          continue;
        }

        if (planned.disposition === "scope-existing-object" && planned.existingObjectId) {
          if (options.session) {
            const bound = await this.designScope.bindObjectScope(options.session, planned.existingObjectId, {
              action: planned.action,
            });
            if (bound) {
              createdFeatureIds.push(bound.featureId);
              produced.set(planned.itemId, bound.featureId);
              scopedObjectIds.push(planned.existingObjectId);
              await this.stamp(bound.featureId, planned.existingObjectId, applicationId, now);
              await this.applyPreferences(bound.featureId, planned, projectId);
            }
          }
          // Without a session the object stays pending; the Design workflow
          // reconciles it. Nothing is created behind the plan's back.
          continue;
        }

        if (planned.disposition === "needs-placement") {
          // Guided placement creates the object (and its scope) in Design.
          continue;
        }

        // Ordinary room-bound scope.
        const feature = await this.features.create(projectId, {
          designId,
          roomId,
          floorId: null,
          featureDefinitionId: planned.item.featureDefinitionId,
          name: nameFor(planned, context.featureDefinitionNames),
          action: planned.action,
          quantityX100: planned.quantityX100,
          quantityUnit: planned.quantityUnit,
          notes: planned.item.notes,
        });
        /*
         * A DEFAULT IS NOT SOMETHING SOMEBODY ENTERED (spec §90-27). The
         * package's own "1 each" used to read as "entered" on the row. It is
         * recorded as coming from the package; the moment the user changes
         * it, features.update writes 'manual' and it becomes theirs.
         */
        if (planned.quantityX100 !== null && !typedQuantities.has(planned.itemId)) {
          await this.features.markQuantityFromPackage(feature.id);
        }
        createdFeatureIds.push(feature.id);
        produced.set(planned.itemId, feature.id);
        await this.db.execute(
          "UPDATE project_features SET package_application_id = ?, updated_at = ? WHERE id = ?",
          [applicationId, now, feature.id],
        );
        if (planned.geometryBinding !== null) {
          // The package attaches its own assembly just below; letting the
          // binding attach a default too priced the work twice (§88B).
          await this.designScope.bindFeatureQuantity(feature.id, planned.geometryBinding, { attachDefaultAssembly: false });
        }
        const templateId = planned.item.assemblyTemplateId ?? (await this.singleCompatibleTemplate(planned));
        // Spatial work entered without a drawing and still waiting for its run
        // length or area gets no assembly yet: it attaches (once, by the
        // Phase 8.1 rule) the moment the quantity is typed, instead of sitting
        // there as an assembly that cannot calculate (spec §89).
        const awaitingQuantity =
          planned.item.objectDefinitionId !== null && planned.geometryBinding === null && planned.effectiveQuantityX100 === null;
        if (templateId && !awaitingQuantity) {
          try {
            await this.assemblies.attachTemplate(feature.id, templateId);
          } catch (err) {
            // An unattachable assembly is reported by the estimate as
            // "Needs assembly" rather than failing the whole application.
            console.error("Package assembly attach failed:", err);
          }
        }
        await this.applyPreferences(feature.id, planned, projectId);
      }

      // Record which scope each item produced and what quantity the package
      // set, so removal can tell the package's own configuration apart from
      // a change the user made afterwards.
      if (produced.size > 0) {
        const finalSnapshot = snapshot.map((entry) => ({ ...entry, featureId: produced.get(entry.itemId) ?? null }));
        await this.db.execute("UPDATE package_applications SET item_snapshot_json = ?, updated_at = ? WHERE id = ?", [
          JSON.stringify(finalSnapshot),
          now,
          applicationId,
        ]);
      }
    });

    return {
      applicationId,
      createdFeatureIds,
      reusedFeatureIds,
      scopedObjectIds,
      pendingPlacements: await this.pendingPlacements(applicationId),
    };
  }

  private async stamp(featureId: string, objectId: string | null, applicationId: string, now: string): Promise<void> {
    await this.db.execute(
      "UPDATE project_features SET package_application_id = COALESCE(package_application_id, ?), updated_at = ? WHERE id = ?",
      [applicationId, now, featureId],
    );
    if (objectId) {
      await this.db.execute(
        "UPDATE plan_objects SET package_application_id = COALESCE(package_application_id, ?), updated_at = ? WHERE id = ?",
        [applicationId, now, objectId],
      );
    }
  }

  /**
   * A package's preferred product is applied through the EXISTING selection
   * rules (copy-on-use snapshot), so a later price change never rewrites
   * what was applied. No preference = the material is chosen the normal way.
   */
  private async applyPreferences(featureId: string, planned: PlannedItem, _projectId: string): Promise<void> {
    if (planned.item.preferredProductId === null) return;
    const instances = await this.assemblies.listByFeature(featureId);
    for (const instance of instances) {
      for (const component of instance.components) {
        if (component.componentType !== "material" || component.archivedAt !== null) continue;
        const product = await this.catalog.getProduct(planned.item.preferredProductId);
        if (!product) return;
        if (component.materialDefinitionId !== null && component.materialDefinitionId !== product.materialDefinitionId) continue;
        try {
          await this.catalog.selectProduct(component.id, product.id, null);
        } catch (err) {
          console.error("Package product preference could not be applied:", err);
        }
        return;
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Guided placement                                                    */
  /* ------------------------------------------------------------------ */

  /** Objects this application still expects the user to place in Design. */
  async pendingPlacements(applicationId: string): Promise<PendingPlacement[]> {
    const rows = await this.db.select<{ item_snapshot_json: string; design_id: string | null; room_id: string | null }>(
      "SELECT item_snapshot_json, design_id, room_id FROM package_applications WHERE id = ? AND removed_at IS NULL",
      [applicationId],
    );
    if (rows.length === 0) return [];
    let snapshot: ItemSnapshot[] = [];
    try {
      snapshot = JSON.parse(rows[0].item_snapshot_json) as ItemSnapshot[];
    } catch {
      return [];
    }
    const names = new Map(
      (await this.db.select<{ id: string; display_name: string }>("SELECT id, display_name FROM object_definitions")).map((d) => [
        d.id,
        d.display_name,
      ]),
    );
    const placed = await this.db.select<{ definition_id: string; n: number }>(
      `SELECT definition_id, COUNT(*) AS n FROM plan_objects
        WHERE package_application_id = ? AND archived_at IS NULL GROUP BY definition_id`,
      [applicationId],
    );
    const placedByDefinition = new Map(placed.map((p) => [p.definition_id, Number(p.n)]));
    const out: PendingPlacement[] = [];
    for (const item of snapshot) {
      if (item.objectDefinitionId === null) continue;
      const expected = item.disposition === "needs-placement" ? item.objectCount : 0;
      const done = placedByDefinition.get(item.objectDefinitionId) ?? 0;
      const remaining = expected - done;
      if (remaining > 0) {
        out.push({
          itemId: item.itemId,
          objectDefinitionId: item.objectDefinitionId,
          objectDefinitionName: names.get(item.objectDefinitionId) ?? "Object",
          featureDefinitionId: item.featureDefinitionId,
          action: item.action,
          remaining,
        });
      }
    }
    return out;
  }

  /** Applications for a room that still have placement work outstanding. */
  async openApplications(projectId: string): Promise<{ id: string; roomId: string | null; packageName: string; pending: PendingPlacement[] }[]> {
    const rows = await this.db.select<{ id: string; room_id: string | null; package_name_snapshot: string }>(
      `SELECT id, room_id, package_name_snapshot FROM package_applications
        WHERE project_id = ? AND removed_at IS NULL ORDER BY applied_at DESC LIMIT 20`,
      [projectId],
    );
    const out = [];
    for (const row of rows) {
      const pending = await this.pendingPlacements(row.id);
      if (pending.length > 0) {
        out.push({ id: row.id, roomId: row.room_id, packageName: row.package_name_snapshot, pending });
      }
    }
    return out;
  }

  /**
   * What this application currently adds to the estimate — the sum of the
   * project's own proposed lines for the scope it produced. There is no
   * package total of its own to drift from the estimate.
   */
  async applicationTotalCents(applicationId: string): Promise<Cents> {
    const rows = await this.db.select<{ total: number | null }>(
      `SELECT SUM(l.amount_cents) AS total FROM proposed_cost_lines l
         JOIN project_features f ON f.id = l.feature_id
        WHERE f.package_application_id = ? AND l.archived_at IS NULL AND f.archived_at IS NULL`,
      [applicationId],
    );
    return Number(rows[0]?.total ?? 0);
  }

  /** Record that a just-placed object belongs to this package application. */
  async attributePlacement(applicationId: string, objectId: string, featureId: string | null): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE plan_objects SET package_application_id = COALESCE(package_application_id, ?), updated_at = ? WHERE id = ?",
      [applicationId, now, objectId],
    );
    if (featureId) {
      await this.db.execute(
        "UPDATE project_features SET package_application_id = COALESCE(package_application_id, ?), updated_at = ? WHERE id = ?",
        [applicationId, now, featureId],
      );
    }
  }

  /* ------------------------------------------------------------------ */
  /* Provenance, removal, save-as-package                                */
  /* ------------------------------------------------------------------ */

  async applicationsForProject(projectId: string): Promise<
    { id: string; roomId: string | null; packageId: string | null; packageName: string; finishTier: string | null; appliedAt: string; removedAt: string | null; featureCount: number }[]
  > {
    const rows = await this.db.select<{
      id: string; room_id: string | null; package_id: string | null; package_name_snapshot: string;
      finish_tier_snapshot: string | null; applied_at: string; removed_at: string | null; n: number;
    }>(
      `SELECT a.*, (SELECT COUNT(*) FROM project_features f WHERE f.package_application_id = a.id AND f.archived_at IS NULL) AS n
         FROM package_applications a WHERE a.project_id = ? ORDER BY a.applied_at DESC`,
      [projectId],
    );
    return rows.map((r) => ({
      id: r.id,
      roomId: r.room_id,
      packageId: r.package_id,
      packageName: r.package_name_snapshot,
      finishTier: r.finish_tier_snapshot,
      appliedAt: r.applied_at,
      removedAt: r.removed_at,
      featureCount: Number(r.n),
    }));
  }

  /**
   * What "Remove package scope" would do — shown BEFORE anything is
   * removed. Work the user modified, or that actual money is attached to,
   * is never destroyed by an undo of a planning action.
   */
  async removalPreview(applicationId: string): Promise<{
    removable: { featureId: string; name: string }[];
    protectedItems: { featureId: string; name: string; reason: string }[];
  }> {
    const features = await this.db.select<{ id: string; name: string }>(
      "SELECT id, name FROM project_features WHERE package_application_id = ? AND archived_at IS NULL",
      [applicationId],
    );
    const appliedQuantity = await this.appliedQuantities(applicationId);
    const removable: { featureId: string; name: string }[] = [];
    const protectedItems: { featureId: string; name: string; reason: string }[] = [];
    for (const feature of features) {
      const reason = await this.protectionReason(feature.id, appliedQuantity.get(feature.id) ?? null, applicationId);
      if (reason === null) removable.push({ featureId: feature.id, name: feature.name });
      else protectedItems.push({ featureId: feature.id, name: feature.name, reason });
    }
    return { removable, protectedItems };
  }

  /** What the package itself set, per feature it produced. */
  private async appliedQuantities(applicationId: string): Promise<Map<string, number | null>> {
    const rows = await this.db.select<{ item_snapshot_json: string }>(
      "SELECT item_snapshot_json FROM package_applications WHERE id = ?",
      [applicationId],
    );
    const out = new Map<string, number | null>();
    if (rows.length === 0) return out;
    try {
      for (const entry of JSON.parse(rows[0].item_snapshot_json) as ItemSnapshot[]) {
        if (entry.featureId) out.set(entry.featureId, entry.quantityX100 ?? null);
      }
    } catch {
      /* Unreadable provenance: every quantity counts as user-set (safer). */
    }
    return out;
  }

  /** Why a feature must survive removal (null = safe to remove). */
  private async protectionReason(
    featureId: string,
    appliedQuantityX100: number | null,
    applicationId: string,
  ): Promise<string | null> {
    const n = async (sql: string): Promise<number> =>
      Number((await this.db.select<{ n: number }>(sql, [featureId]))[0]?.n ?? 0);
    if ((await n("SELECT COUNT(*) AS n FROM actual_expenses WHERE feature_id = ? AND voided_at IS NULL")) > 0) {
      return "Actual costs are recorded against it.";
    }
    if (
      (await n(
        `SELECT COUNT(*) AS n FROM actual_expenses WHERE voided_at IS NULL AND linked_proposed_line_id IN
           (SELECT id FROM proposed_cost_lines WHERE feature_id = ?)`,
      )) > 0
    ) {
      return "Actual costs are recorded against its estimate lines.";
    }
    if ((await n("SELECT COUNT(*) AS n FROM purchase_allocations WHERE linked_feature_id = ?")) > 0) {
      return "Purchases are allocated to it.";
    }
    if (
      (await n(
        `SELECT COUNT(*) AS n FROM purchase_allocations WHERE linked_proposed_line_id IN
           (SELECT id FROM proposed_cost_lines WHERE feature_id = ?)`,
      )) > 0
    ) {
      return "Purchases are allocated to its estimate lines.";
    }
    if ((await n("SELECT COUNT(*) AS n FROM proposed_cost_lines WHERE feature_id = ? AND source = 'manual' AND archived_at IS NULL")) > 0) {
      return "It has cost lines you added by hand.";
    }
    // The quantity the PACKAGE set is not a user modification; a different
    // one is.
    const quantity = await this.db.select<{ quantity_x100: number | null }>(
      "SELECT quantity_x100 FROM project_features WHERE id = ?",
      [featureId],
    );
    const current = quantity[0]?.quantity_x100 ?? null;
    if (current !== null && Number(current) !== appliedQuantityX100) {
      return "You set its quantity by hand.";
    }
    // Objects the user placed themselves are Design content: geometry is
    // never destroyed from outside the plan.
    const foreign = await this.db.select<{ n: number }>(
      `SELECT COUNT(*) AS n FROM plan_objects
        WHERE feature_id = ? AND archived_at IS NULL
          AND (package_application_id IS NULL OR package_application_id <> ?)`,
      [featureId, applicationId],
    );
    if (Number(foreign[0]?.n ?? 0) > 0) {
      return "Objects you placed in the Design are bound to it — remove them in Design.";
    }
    if ((await n("SELECT COUNT(*) AS n FROM estimate_evidence_decisions WHERE component_id IN (SELECT c.id FROM assembly_instance_components c JOIN assembly_instances i ON i.id = c.instance_id WHERE i.project_feature_id = ?)")) > 0) {
      return "You priced part of it from cost evidence.";
    }
    return null;
  }

  /**
   * Remove what the package created and nothing else. Untouched planning
   * work is archived; anything the user changed or attached money to stays,
   * and simply stops being package-owned.
   */
  async removeApplication(
    applicationId: string,
    options: { session?: ScopeSession | null } = {},
  ): Promise<{ removed: number; kept: number; objectsRemoved: number }> {
    let objectsRemoved = 0;
    // Objects the package itself placed are removed through the geometry
    // operation API (Design stays master); objects the user placed are
    // never touched, and without a session nothing geometric happens.
    if (options.session) {
      const placed = await this.db.select<{ id: string }>(
        "SELECT id FROM plan_objects WHERE package_application_id = ? AND archived_at IS NULL",
        [applicationId],
      );
      const model = options.session.snapshot().model;
      for (const row of placed) {
        if (!model.objects.has(row.id)) continue;
        options.session.commit("Remove package object", (m, ctx) => archiveObject(m, ctx, row.id));
        objectsRemoved += 1;
      }
      if (objectsRemoved > 0) await options.session.flush();
    }
    const preview = await this.removalPreview(applicationId);
    const now = new Date().toISOString();
    await this.db.transaction(async () => {
      for (const item of preview.removable) {
        await this.designScope.archiveGeneratedLines(item.featureId, now);
        await this.db.execute(
          "UPDATE proposed_cost_lines SET archived_at = COALESCE(archived_at, ?), updated_at = ? WHERE feature_id = ?",
          [now, now, item.featureId],
        );
        await this.features.archive(item.featureId);
      }
      // Whatever survives is ordinary project scope from now on.
      await this.db.execute(
        "UPDATE project_features SET package_application_id = NULL, updated_at = ? WHERE package_application_id = ?",
        [now, applicationId],
      );
      await this.db.execute(
        "UPDATE plan_objects SET package_application_id = NULL, updated_at = ? WHERE package_application_id = ?",
        [now, applicationId],
      );
      await this.db.execute("UPDATE package_applications SET removed_at = ?, updated_at = ? WHERE id = ?", [now, now, applicationId]);
    });
    return { removed: preview.removable.length, kept: preview.protectedItems.length, objectsRemoved };
  }

  /**
   * Save a room's configured scope as a reusable library package.
   *
   * The project is NOT linked to the new package: it keeps the scope it
   * already has, and the package is a fresh library entry that future
   * projects can apply.
   */
  async saveRoomAsPackage(
    projectId: string,
    roomId: string,
    input: { name: string; description: string; finishTier: ScopePackage["finishTier"] },
  ): Promise<ScopePackage> {
    const room = await this.rooms.getById(roomId);
    if (!room) throw new ValidationError(["That room no longer exists."]);
    const designId = (
      await this.db.select<{ active_design_id: string | null }>("SELECT active_design_id FROM projects WHERE id = ?", [projectId])
    )[0]?.active_design_id ?? null;
    const features = (await this.features.listByProject(projectId)).filter(
      (f) => f.archivedAt === null && f.roomId === roomId && (f.designId === null || f.designId === designId),
    );
    if (features.length === 0) {
      throw new ValidationError(["This room has no scope to save yet."]);
    }
    const created = await this.packages.create({
      name: input.name,
      kind: "room",
      description: input.description,
      finishTier: input.finishTier,
      roomTypeIds: room.roomTypeId ? [room.roomTypeId] : [],
    });

    for (const feature of features) {
      const instances = await this.assemblies.listByFeature(feature.id);
      const objects = await this.db.select<{ definition_id: string; n: number }>(
        `SELECT definition_id, COUNT(*) AS n FROM plan_objects
          WHERE feature_id = ? AND archived_at IS NULL GROUP BY definition_id`,
        [feature.id],
      );
      const preferred = await this.db.select<{ product_id: string }>(
        `SELECT s.product_id FROM project_product_selections s
           JOIN assembly_instance_components c ON c.id = s.component_id
           JOIN assembly_instances i ON i.id = c.instance_id
          WHERE i.project_feature_id = ? AND s.replaced_at IS NULL LIMIT 1`,
        [feature.id],
      );
      const item: ScopePackageItemInput = {
        itemType: "feature",
        featureDefinitionId: feature.featureDefinitionId,
        action: feature.action,
        objectDefinitionId: objects[0]?.definition_id ?? null,
        objectCount: objects[0] ? Number(objects[0].n) : 1,
        assemblyTemplateId: instances[0]?.templateId ?? null,
        quantitySource:
          feature.quantityGeometryBinding === "room-floor-area" ||
          feature.quantityGeometryBinding === "room-perimeter" ||
          feature.quantityGeometryBinding === "room-wall-area" ||
          feature.quantityGeometryBinding === "room-ceiling-area"
            ? feature.quantityGeometryBinding
            : feature.quantityGeometryBinding === "objects-count"
                ? "objects-count"
                : feature.quantityX100 !== null
                  ? "manual"
                  : "default",
        quantityX100: feature.quantityGeometryBinding === null ? feature.quantityX100 : null,
        quantityUnit: feature.quantityGeometryBinding === null ? feature.quantityUnit : null,
        preferredProductId: preferred[0]?.product_id ?? null,
        inclusion: "required",
        childPackageId: null,
        notes: "",
      };
      if (item.featureDefinitionId === null && item.objectDefinitionId === null) continue; // one-off custom scope has nothing reusable to point at
      await this.packages.addItem(created.id, item);
    }
    return (await this.packages.getById(created.id))!;
  }
}

function unitForPlanned(planned: PlannedItem): string | null {
  if (planned.quantityUnit) return planned.quantityUnit;
  if (planned.item.quantityUnit) return planned.item.quantityUnit;
  switch (planned.geometryBinding) {
    case "room-floor-area":
    case "room-wall-area":
    case "room-ceiling-area":
      return "sqft";
    case "room-perimeter":
      return "lf";
    case "objects-count":
      return "each";
    default:
      return planned.item.objectDefinitionId !== null ? "each" : null;
  }
}

function nameFor(planned: PlannedItem, names: Map<string, string>): string {
  const base = planned.item.featureDefinitionId ? names.get(planned.item.featureDefinitionId) : null;
  if (!base) return "Package scope";
  const actionWord = planned.action === "install" ? "Install" : planned.action === "replace" ? "Replace" : null;
  if (!actionWord) return base;
  return /^(replace|install)/i.test(base) ? base.replace(/^(Replace|Install)/i, actionWord) : base;
}
